import { Link } from 'react-router-dom';
import { formatFeedDate, formatStars, rankingDateLabel, ratingGroupHeadline } from '@aluvo/constants/src/homeFeed';
import { FoundingCollectorIcon } from '../FoundingCollectorBadge/FoundingCollectorBadge';
import styles from './HomeFeed.module.css';

// Card renderers for the home feed (docs/home-feed.md). Every action calls
// back into HomeFeed, which owns navigation analytics and Watchlist state.

const RANKING_PREVIEW = 5;
const COMPOSITION_LABEL = { movie: 'Movies', tv: 'TV shows', mixed: 'Movies & TV' };

function CreatorLine({ creator, date, children }) {
    const initial = (creator.displayName || creator.username || '?')[0].toUpperCase();
    return (
        <div className={styles.creator}>
            {creator.avatarUrl
                ? <img className={styles.avatar} src={creator.avatarUrl} alt="" />
                : <span className={styles.avatarFallback} aria-hidden="true">{initial}</span>}
            <div className={styles.creatorText}>
                <Link to={`/user/${encodeURIComponent(creator.username)}`} className={styles.username}>
                    @{creator.username}
                </Link>
                <FoundingCollectorIcon isFoundingCollector={creator.isFoundingCollector} />
                {children}
                {date && <span className={styles.date}>{date}</span>}
            </div>
        </div>
    );
}

function EditorialLabel({ editorial }) {
    if (!editorial) return null;
    return (
        <div className={styles.editorial}>
            <span className={styles.picksLabel}>✦ {editorial.label || 'Aluvo Picks'}</span>
            {editorial.note && <p className={styles.note}>{editorial.note}</p>}
        </div>
    );
}

export function SaveButton({ title, status, onSave, compact = false }) {
    const saved = status === 'saved';
    const label = saved ? `${title.title} is on your Watchlist` : `Add ${title.title} to your Watchlist`;
    return (
        <button
            type="button"
            className={`${styles.saveBtn} ${saved ? styles.saveBtnSaved : ''} ${compact ? styles.saveBtnCompact : ''}`}
            onClick={() => onSave(title)}
            disabled={saved}
            aria-label={label}
            title={label}
        >
            {saved ? '✓ Saved' : '+ Watchlist'}
        </button>
    );
}

function SaveError({ title, status, onSave }) {
    if (status !== 'error') return null;
    return (
        <p className={styles.saveError} role="alert">
            Couldn&apos;t save {title.title}.{' '}
            <button type="button" className={styles.linkBtn} onClick={() => onSave(title)}>Try again</button>
        </p>
    );
}

function Poster({ title, rank, onOpen }) {
    return (
        <button type="button" className={styles.posterBtn} onClick={() => onOpen(title)} aria-label={`${rank ? `Number ${rank}: ` : ''}${title.title}${title.year ? ` (${title.year})` : ''}`}>
            {title.posterUrl
                ? <img className={styles.poster} src={title.posterUrl} alt="" loading="lazy" />
                : <span className={`${styles.poster} ${styles.posterFallback}`}>{title.title}</span>}
            {rank != null && <span className={styles.rank} aria-hidden="true">{rank}</span>}
        </button>
    );
}

function TitleTile({ title, rank, statusOf, onOpen, onSave }) {
    const status = statusOf(title);
    return (
        <li className={styles.tile}>
            <Poster title={title} rank={rank} onOpen={onOpen} />
            <span className={styles.tileTitle} title={title.title}>{title.title}</span>
            <SaveButton title={title} status={status} onSave={onSave} compact />
            <SaveError title={title} status={status} onSave={onSave} />
        </li>
    );
}

export function RankingCard({ card, statusOf, onOpenRanking, onOpenTitle, onSave }) {
    const { ranking, creator } = card;
    const preview = ranking.items.slice(0, RANKING_PREVIEW);
    const more = ranking.entryCount - preview.length;
    return (
        <article className={styles.card} aria-label={`Ranking: ${ranking.title}`}>
            <EditorialLabel editorial={card.editorial} />
            <CreatorLine creator={creator} date={rankingDateLabel(ranking)} />
            <p className={styles.kicker}>
                Top {ranking.size}{COMPOSITION_LABEL[ranking.composition] ? ` · ${COMPOSITION_LABEL[ranking.composition]}` : ''} · {ranking.entryCount} titles
            </p>
            <h3 className={styles.cardTitle}>{ranking.title}</h3>
            <ul className={styles.tiles}>
                {preview.map((item) => (
                    <TitleTile
                        key={`${item.kind}:${item.tmdbId}`}
                        title={item}
                        rank={item.rank}
                        statusOf={statusOf}
                        onOpen={onOpenTitle}
                        onSave={onSave}
                    />
                ))}
            </ul>
            <div className={styles.actions}>
                <button type="button" className={styles.primaryBtn} onClick={() => onOpenRanking(card)}>
                    View ranking{more > 0 ? ` (+${more} more)` : ''}
                </button>
            </div>
        </article>
    );
}

export function EditorialTitleCard({ card, statusOf, onOpenTitle, onSave }) {
    const { title } = card;
    const status = statusOf(title);
    return (
        <article className={`${styles.card} ${styles.titleCard}`} aria-label={`Aluvo Pick: ${title.title}`}>
            <Poster title={title} onOpen={onOpenTitle} />
            <div className={styles.titleCardBody}>
                <EditorialLabel editorial={card.editorial} />
                <h3 className={styles.cardTitle}>{title.title}</h3>
                <p className={styles.meta}>{title.kind === 'tv' ? 'TV series' : 'Movie'}{title.year ? ` · ${title.year}` : ''}</p>
                <div className={styles.actions}>
                    <button type="button" className={styles.primaryBtn} onClick={() => onOpenTitle(title)}>View details</button>
                    <SaveButton title={title} status={status} onSave={onSave} />
                </div>
                <SaveError title={title} status={status} onSave={onSave} />
            </div>
        </article>
    );
}

export function EditorialCollectionCard({ card, onOpenEditorial }) {
    const { collection } = card;
    return (
        <article className={`${styles.card} ${styles.titleCard}`} aria-label={`Aluvo Pick: ${collection.name}`}>
            {collection.imageUrl
                ? <img className={styles.coin} src={collection.imageUrl} alt="" />
                : <span className={`${styles.coin} ${styles.posterFallback}`} aria-hidden="true">🏛️</span>}
            <div className={styles.titleCardBody}>
                <EditorialLabel editorial={card.editorial} />
                <h3 className={styles.cardTitle}>{collection.name}</h3>
                <p className={styles.meta}>Collection · {collection.itemCount} titles{collection.rewardCoinName ? ` · earns ${collection.rewardCoinName}` : ''}</p>
                <div className={styles.actions}>
                    <button type="button" className={styles.primaryBtn} onClick={() => onOpenEditorial(card, `/collections/${collection.slug}`)}>View collection</button>
                </div>
            </div>
        </article>
    );
}

export function EditorialListCard({ card, onOpenEditorial }) {
    const { list } = card;
    return (
        <article className={styles.card} aria-label={`Aluvo Pick: ${list.title}`}>
            <EditorialLabel editorial={card.editorial} />
            <h3 className={styles.cardTitle}>{list.title}</h3>
            {list.description && <p className={styles.meta}>{list.description}</p>}
            {list.posters.length > 0 && (
                <div className={styles.posterStrip} aria-hidden="true">
                    {list.posters.map((url) => <img key={url} className={styles.poster} src={url} alt="" loading="lazy" />)}
                </div>
            )}
            <div className={styles.actions}>
                <button type="button" className={styles.primaryBtn} onClick={() => onOpenEditorial(card, `/discover/lists/${list.slug}`)}>
                    View list · {list.itemCount} titles
                </button>
            </div>
        </article>
    );
}

export function RecommendationRail({ card, statusOf, onOpenRecommendation, onSave }) {
    return (
        <section className={styles.card} aria-label={card.reason.text}>
            <p className={card.source === 'editorial' ? styles.picksLabel : styles.reason}>{card.reason.text}</p>
            <ul className={`${styles.tiles} ${styles.rail}`}>
                {card.items.map((item) => (
                    <TitleTile
                        key={`${item.kind}:${item.tmdbId}`}
                        title={item}
                        statusOf={statusOf}
                        onOpen={(title) => onOpenRecommendation(card, title)}
                        onSave={(title) => onSave(title, card)}
                    />
                ))}
            </ul>
        </section>
    );
}

export function RatingGroupCard({ card, onOpenTitle }) {
    const { creator, ratings } = card;
    const more = card.total - ratings.length;
    return (
        <article className={styles.card} aria-label={`${creator.username}: ${ratingGroupHeadline(card.total)}`}>
            <CreatorLine creator={creator} date={formatFeedDate(card.occurredAt)}>
                <span className={styles.action}>{ratingGroupHeadline(card.total)}</span>
            </CreatorLine>
            <ul className={styles.ratings}>
                {ratings.map((r) => (
                    <li key={r.tmdbId} className={styles.ratingRow}>
                        <Poster title={r} onOpen={onOpenTitle} />
                        <div>
                            <button type="button" className={styles.linkBtn} onClick={() => onOpenTitle(r)}>{r.title}</button>
                            {r.year && <span className={styles.meta}> · {r.year}</span>}
                            <p className={styles.stars} aria-label={`${formatStars(r.rating)} out of ${card.scale.max} stars`}>
                                <span aria-hidden="true">★ {formatStars(r.rating)}</span>
                                <span className={styles.scale} aria-hidden="true"> / {card.scale.max}</span>
                            </p>
                        </div>
                    </li>
                ))}
            </ul>
            <div className={styles.actions}>
                <Link className={styles.secondaryLink} to={`/user/${encodeURIComponent(creator.username)}/ratings`}>
                    {more > 0 ? `See all ${card.total} ratings` : 'See their ratings'}
                </Link>
            </div>
        </article>
    );
}
